import { useEffect, useState } from 'react';
import {
  FAMILY_RELATION_WORDS,
  FAMILY_RELATIONS,
  familyTieSentence,
  type FamilyRelation,
  type KnowledgeEntry,
  type KnowledgeKind,
  type KnowledgeLinkResponse,
} from '@ni/shared';
import { useKnowledge, useLinkFamily, useLinkKnowledge } from '../../api/hooks';
import { errorLine, isNotFound, validationSentence } from '../../api/errors';
import { Modal } from '../ui/modal';
import { WorkingButton } from '../ui/working-button';
import { SEARCH_DEBOUNCE_MS, useSettled } from '../../lib/debounce';
import { isKnowledgeSearch } from '../../lib/knowledge';
import {
  familyLinkBody,
  isFamilyLink,
  linkBody,
  linkSearchKinds,
  linkCandidates,
  linkReadings,
  linkSentence,
  readingKey,
  sameReading,
  type LinkReading,
} from '../../lib/knowledge-link';
import {
  CANCEL,
  KNOWLEDGE_FAMILY_PICKER_NOTE,
  KNOWLEDGE_FAMILY_PLACEHOLDER,
  KNOWLEDGE_KIND_WORDS,
  KNOWLEDGE_LINK_ACTION,
  KNOWLEDGE_LINK_ALREADY,
  KNOWLEDGE_LINK_CHANGE,
  KNOWLEDGE_LINK_DONE,
  KNOWLEDGE_LINK_GONE,
  KNOWLEDGE_LINK_NONE_MATCH,
  KNOWLEDGE_LINK_NO_READING,
  KNOWLEDGE_LINK_PICKER_NOTE,
  KNOWLEDGE_LINK_PREVIEW_LEAD,
  KNOWLEDGE_LINK_RELATION_ASK,
  KNOWLEDGE_LINK_SAVE,
  KNOWLEDGE_LINK_SEARCHING,
  KNOWLEDGE_LINK_SEARCH_LABEL,
  KNOWLEDGE_LINK_SEARCH_PLACEHOLDER,
  KNOWLEDGE_LINK_TYPE_MORE,
  KNOWLEDGE_LINK_WORKING,
  knowledgeConnections,
  knowledgeFamilyAsk,
  knowledgeLinkChosen,
  knowledgeLinkLead,
} from '../../lib/copy';
import { EmptyLine } from '../ui/empty-line';
import { Button } from '../ui/button';
import './link-modal.css';

/**
 * **Link to another record** (organisation-profile note §1.1) — the knowledge
 * base's one write, and the only control on the panel that changes anything.
 *
 * Three steps and no free text: search the records already on file, pick one,
 * pick which of the sentences the two of them can stand in is true. The sentence
 * is shown in full before it is sent, because the direction is the whole
 * question — "University of Texas at Austin is part of University of Texas
 * System" and its opposite are one button apart, and only the words tell them
 * apart.
 *
 * **A picker rather than a box**, because the point of the gesture is to land on
 * the *exact* existing record: a name typed here would be a second door for
 * minting one, and the link would then be to whatever the typing resolved to.
 *
 * The view is split from the writer the way `MergeCandidateCardView` is, and for
 * the same reason: this package's suite has no DOM, so what is on the page is
 * asserted against the view's markup and what goes on the wire is asserted
 * against `linkBody` in `lib/knowledge-link.spec.ts`.
 *
 * **From a person's panel it asks how another person is related** (2026-09-30):
 * people only in the search, and one list of family words in place of the
 * sentences, with the tie read back in full before it is sent.
 */
export function LinkRecordModalView({
  here,
  results,
  typed,
  searching,
  chosen,
  reading,
  family = null,
  working,
  error,
  recorded,
  onTyped,
  onChoose,
  onClear,
  onPickReading,
  onPickFamily = () => {},
  onSave,
  onClose,
}: {
  /** The record whose page this was opened from — one half of every sentence. */
  here: { entity_id: string; display_name: string; kind: KnowledgeKind };
  /** What the search came back with, already stripped of `here` itself. */
  results: readonly KnowledgeEntry[];
  typed: string;
  searching: boolean;
  /** The record picked out of the results, or null while the search is open. */
  chosen: KnowledgeEntry | null;
  /** The sentence picked, or null while none is. */
  reading: LinkReading | null;
  /** On a person's panel: what the chosen person is to this one, or null while none is. */
  family?: FamilyRelation | null;
  working: boolean;
  error: unknown;
  /** Set once the link has landed: what the record now says, and whether it already did. */
  recorded: { sentence: string; already: boolean } | null;
  onTyped: (next: string) => void;
  onChoose: (entry: KnowledgeEntry) => void;
  onClear: () => void;
  onPickReading: (reading: LinkReading) => void;
  onPickFamily?: (relation: FamilyRelation) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const familyLink = isFamilyLink(here.kind);
  const readings = chosen === null || familyLink ? [] : linkReadings(here.kind, chosen.kind);
  /* **The one refusal this door has to say in its own words.** The pair checks
     are `VALIDATION_FAILED`s carrying a sentence somebody can act on, and those
     are shown as the server wrote them. A 404 is the exception: it is the record
     picked out of the search having been merged or removed between the picking
     and the pressing, and the envelope's own message for it is the bare word
     "Not found", which is a fragment rather than an instruction. The reader's
     next move — search again — is what the line has to carry. */
  const line = isNotFound(error)
    ? KNOWLEDGE_LINK_GONE
    : (validationSentence(error) ?? errorLine(error));

  return (
    <Modal label={KNOWLEDGE_LINK_ACTION} onClose={onClose}>
      <div className="modal-head">{KNOWLEDGE_LINK_ACTION}</div>
      <div className="modal-line">{knowledgeLinkLead(here.display_name)}</div>

      <div className="link-modal">
        {chosen === null ? (
          <>
            <div className="field search-field">
              <label className="label" htmlFor="knowledge-link-q">
                {KNOWLEDGE_LINK_SEARCH_LABEL}
              </label>
              <input
                id="knowledge-link-q"
                type="search"
                autoComplete="off"
                placeholder={KNOWLEDGE_LINK_SEARCH_PLACEHOLDER}
                value={typed}
                onChange={(event) => onTyped(event.target.value)}
              />
            </div>
            {/* Said under the box rather than in the placeholder: it is a rule
                about what this door does, not a hint about what to type. */}
            <div className="src">
              {familyLink ? KNOWLEDGE_FAMILY_PICKER_NOTE : KNOWLEDGE_LINK_PICKER_NOTE}
            </div>

            {!isKnowledgeSearch(typed) ? (
              <EmptyLine>{KNOWLEDGE_LINK_TYPE_MORE}</EmptyLine>
            ) : searching ? (
              <EmptyLine>{KNOWLEDGE_LINK_SEARCHING}</EmptyLine>
            ) : results.length === 0 ? (
              <EmptyLine>{KNOWLEDGE_LINK_NONE_MATCH}</EmptyLine>
            ) : (
              <div className="link-results">
                {results.map((entry) => (
                  <button
                    key={entry.entity_id}
                    type="button"
                    className="knowledge-touch"
                    onClick={() => onChoose(entry)}
                  >
                    <span className="knowledge-touch-name">{entry.display_name}</span>
                    <span className="src">
                      {[
                        KNOWLEDGE_KIND_WORDS[entry.kind],
                        knowledgeConnections(entry.connections),
                      ].join(' · ')}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="modal-line">{knowledgeLinkChosen(chosen.display_name)}</div>
            <button type="button" className="quiet-action" onClick={onClear} disabled={working}>
              {KNOWLEDGE_LINK_CHANGE}
            </button>

            {familyLink ? (
              <>
                <div className="field">
                  <label className="label" htmlFor="knowledge-family-relation">
                    {knowledgeFamilyAsk(chosen.display_name, here.display_name)}
                  </label>
                  <select
                    id="knowledge-family-relation"
                    value={family ?? ''}
                    disabled={working}
                    onChange={(event) => onPickFamily(event.target.value as FamilyRelation)}
                  >
                    <option value="" disabled>
                      {KNOWLEDGE_FAMILY_PLACEHOLDER}
                    </option>
                    {FAMILY_RELATIONS.map((relation) => (
                      <option key={relation} value={relation}>
                        {FAMILY_RELATION_WORDS[relation].noun}
                      </option>
                    ))}
                  </select>
                </div>
                {family ? (
                  <div className="relate-preview" aria-live="polite">
                    <div className="relate-lead">{KNOWLEDGE_LINK_PREVIEW_LEAD}</div>
                    <div className="relate-sentence">
                      {familyTieSentence(family, chosen.display_name, here.display_name)}
                    </div>
                  </div>
                ) : null}
              </>
            ) : readings.length === 0 ? (
              /* Two events, or anything with a person in it. There is no such
                 fact to write, so the modal says so rather than offering a
                 sentence whose only outcome is the server's refusal. */
              <EmptyLine>{KNOWLEDGE_LINK_NO_READING}</EmptyLine>
            ) : (
              <>
                <div className="relate-ask">{KNOWLEDGE_LINK_RELATION_ASK}</div>
                {/* **The readings are the sentences**, names filled in. The
                    direction is not a second control: "A is part of B" and "B is
                    part of A" are two of these, which is the only way a reader
                    can be sure which way round they have said it. */}
                <div className="pills" role="group" aria-label={KNOWLEDGE_LINK_RELATION_ASK}>
                  {readings.map((option) => {
                    const on = reading !== null && sameReading(option, reading);
                    return (
                      <button
                        key={readingKey(option)}
                        type="button"
                        className={on ? 'pill on' : 'pill'}
                        aria-pressed={on}
                        disabled={working}
                        onClick={() => onPickReading(option)}
                      >
                        {linkSentence(option, here.display_name, chosen.display_name)}
                      </button>
                    );
                  })}
                </div>

                {/* What is about to be recorded, word for word, and the same
                    string the answer comes back with: the server echoes the
                    page's order even where the record files the pair the other
                    way round. Nothing until a sentence is picked: a preview with
                    a blank in it is a sentence nobody said.

                    Set as the inbox card's is — a quiet lead-in over the
                    sentence itself, which is the one line in the step a reader
                    has to get right. The two doors ask one question and confirm
                    it one way. */}
                {reading ? (
                  <div className="relate-preview" aria-live="polite">
                    <div className="relate-lead">{KNOWLEDGE_LINK_PREVIEW_LEAD}</div>
                    <div className="relate-sentence">
                      {linkSentence(reading, here.display_name, chosen.display_name)}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </>
        )}

        {/* The confirmation stays where the reader is looking, and says which of
            the two things happened: a link written, or one that was already
            there. Pressing again is not a failure. */}
        {recorded ? (
          <div className="link-done" aria-live="polite">
            <div>{recorded.already ? KNOWLEDGE_LINK_ALREADY : KNOWLEDGE_LINK_DONE}</div>
            <div className="src">{recorded.sentence}</div>
          </div>
        ) : null}

        {error ? <div className="src">{line}</div> : null}
      </div>

      <div className="modal-actions">
        <WorkingButton
          label={KNOWLEDGE_LINK_SAVE}
          workingLabel={KNOWLEDGE_LINK_WORKING}
          working={working}
          disabled={chosen === null || (familyLink ? family === null : reading === null)}
          onClick={onSave}
        />
        {/* Never disabled while the write runs, as every other confirm in the
            app: walking away is the reader's to do, and the link is idempotent. */}
        <Button variant="act" onClick={onClose}>
          {CANCEL}
        </Button>
      </div>
    </Modal>
  );
}

/** What the modal does with the answer: shut, or stand and say it. */
export type LinkOutcome =
  { close: true } | { close: false; recorded: { sentence: string; already: boolean } };

/**
 * **What the answer does with itself**, as a function, for the reason `linkBody`
 * is one: this package's suite has no click, so the branch the press takes is
 * asserted here rather than through the button.
 *
 * A caller that wants the sentence gets it and the modal shuts — the record page
 * and the knowledge panel both print it under their own head, where the reader
 * is returned to and where the block it changed is. A caller that does not gets
 * what this door has always done: the sentence stands in the modal until it is
 * dismissed.
 */
export function linkRecorded(
  response: KnowledgeLinkResponse,
  onRecorded?: (sentence: string, already: boolean) => void,
): LinkOutcome {
  if (onRecorded) {
    onRecorded(response.sentence, response.already_recorded);
    return { close: true };
  }
  return {
    close: false,
    recorded: { sentence: response.sentence, already: response.already_recorded },
  };
}

/**
 * The writer: the search, the two picks and the one mutation.
 *
 * It owns the debounce the knowledge page's own box owns, and for the same
 * reason — typing a name is one request rather than eight — and it reads through
 * `useKnowledge`, which is the page's existing search under the page's existing
 * key, so a name already looked up on the list behind the modal is already in
 * the cache.
 *
 * **Where the sentence is said is the caller's to choose.** Given `onRecorded`
 * the modal hands the answer up and closes on the press, which is what a page
 * with a "How it stands to others" block behind it wants: the relation is in
 * that block by the time the modal is gone, and the sentence is printed once,
 * under the header, the way an added statement's is. Without it the modal keeps
 * today's behaviour and says the sentence itself.
 */
export function LinkRecordModal({
  here,
  onClose,
  onRecorded,
}: {
  here: { entity_id: string; display_name: string; kind: KnowledgeKind };
  onClose: () => void;
  onRecorded?: (sentence: string, already: boolean) => void;
}) {
  const [typed, setTyped] = useState('');
  const [settled, pushTyped] = useSettled('', SEARCH_DEBOUNCE_MS);
  const [chosen, setChosen] = useState<KnowledgeEntry | null>(null);
  const [reading, setReading] = useState<LinkReading | null>(null);
  const [family, setFamily] = useState<FamilyRelation | null>(null);
  const [recorded, setRecorded] = useState<{ sentence: string; already: boolean } | null>(null);

  const answer = useKnowledge({ q: settled, kinds: linkSearchKinds(here.kind) });
  const familyLink = isFamilyLink(here.kind);
  const orgWrite = useLinkKnowledge(here.entity_id);
  const familyWrite = useLinkFamily(here.entity_id);
  const write = familyLink ? familyWrite : orgWrite;
  const landed = {
    onSuccess: (response: KnowledgeLinkResponse) => {
      const outcome = linkRecorded(response, onRecorded);
      if (outcome.close) onClose();
      else setRecorded(outcome.recorded);
    },
  };

  /* One reading offered is one reading meant — an organisation and an event have
     exactly one thing they can be to each other, and making the office press it
     would be asking a question with one answer. Five readings have no default:
     which way round is the question. */
  const readings = chosen === null || familyLink ? [] : linkReadings(here.kind, chosen.kind);
  useEffect(() => {
    setReading(readings.length === 1 ? (readings[0] as LinkReading) : null);
    setFamily(null);
    // The pick is what this is about; `readings` is derived from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen]);

  /* A search below the minimum asks for nothing (`useKnowledge` sends no `q`
     and the server answers with the most connected), so the results are the
     entries and nothing else — never the opening lists, which are not what was
     asked for. */
  const entries = isKnowledgeSearch(settled) ? (answer.data?.entries ?? []) : [];

  return (
    <LinkRecordModalView
      here={here}
      results={linkCandidates(entries, here.entity_id)}
      typed={typed}
      searching={isKnowledgeSearch(settled) && answer.isPending}
      chosen={chosen}
      reading={reading}
      family={family}
      working={write.isPending}
      error={write.error}
      recorded={recorded}
      onTyped={(next) => {
        setTyped(next);
        pushTyped(next);
      }}
      onChoose={(entry) => {
        if (write.isError) write.reset();
        setRecorded(null);
        setChosen(entry);
      }}
      onClear={() => {
        if (write.isError) write.reset();
        setRecorded(null);
        setChosen(null);
      }}
      onPickReading={(next) => {
        if (write.isError) write.reset();
        setRecorded(null);
        setReading(next);
      }}
      onPickFamily={(next) => {
        if (write.isError) write.reset();
        setRecorded(null);
        setFamily(next);
      }}
      onSave={() => {
        if (chosen === null || write.isPending) return;
        if (familyLink) {
          if (family === null) return;
          familyWrite.mutate(familyLinkBody(family, chosen.entity_id), landed);
          return;
        }
        if (reading === null) return;
        orgWrite.mutate(linkBody(reading, chosen.entity_id), landed);
      }}
      onClose={onClose}
    />
  );
}
